import { DataTable, type Pagination } from '@/components/common/table';
import { useMinistries } from '@/store/org/hooks';
import { type ActionKey, makeColumns } from './columns';
import { useMemo, useState } from 'react';
import { useDisclosure } from '@/components/ui';
import { MinistryModal } from './modal';
import { MinistryTypes } from '@/store/org/types';
import { CampusSelect } from '@/components/common/input/campus';
import { Icon } from '@iconify/react/dist/iconify.js';
import { memoize } from '@/lib/memoize';
import type { Ministry } from '@/shared/types/ministry';

export function Ministries() {
  const { loading, list } = useMinistries(false);
  const [campus, setCampus] = useState('');
  const items = useMemo(() => {
    if (!campus) {
      return [];
    }

    const lookup = memoize(list, (item: Ministry) => item.data.type);

    return MinistryTypes.map((type) => {
      const item =
        lookup[type.key] ||
        ({
          id: '',
          data: { campus_id: campus, type: type.key, active: false },
        } as Ministry);

      return item;
    });
  }, [campus, list]);
  const [editing, setEditing] = useState({ id: '', data: {} } as Ministry);
  const onAction = (item: Ministry, key: ActionKey) => {
    if (key === 'edit') {
      setEditing(item);
      modal.onOpen();
    }
  };

  const onActiveChange = (item: Ministry, value: boolean) => {
    setEditing({
      id: item.id,
      data: { ...item.data, active: value },
    } as Ministry);
    modal.onOpen();
  };

  const modal = useDisclosure();
  const columns = makeColumns(onAction, onActiveChange);
  const [page, setPage] = useState(1);
  const pagination: Pagination = {
    enabled: true,
    absolute: true,
    itemsPerPage: 20,
    page,
    onPageChange: (page: number) => setPage(page),
  };

  return (
    <>
      <div className='mb-4 flex flex-row items-center'>
        <CampusSelect
          placeholder='Select Campus'
          startContent={<Icon icon='mdi:location' width={20} />}
          value={campus}
          onChangeSingle={setCampus}
          className='basis-3/12'
        />
      </div>
      <DataTable
        aria-label='Ministries'
        columns={columns}
        items={items}
        loading={loading}
        className='text-gray-800'
        empty={{ icon: 'mdi-location' }}
        pagination={pagination}
      />
      <MinistryModal disclosure={modal} item={editing} />
    </>
  );
}
